import { basicSort, COMMON_TABLE_COLUMN_WIDTHS, geneLinkSort, numericSort, TableCellInteger, TableCellLink, TableCellPercent, TableCellPValue, TableCellScientificNumeric, TableWidths, viewVariantsSort, } from 'Shared/components/Table';
import { ELevelResult, ETraitsTypes } from 'Shared/types';
import createTableQVLink from 'Common/components/Table/factories/createTableQVLink/createTableQVLink';
import createTableGeneLink from 'Common/components/Table/factories/createTableGeneLink/createTableGeneLink';
import styles from 'Shared/components/Table/components/TableView/TableView.css';
export var ETableColumnAccessor;
(function (ETableColumnAccessor) {
    ETableColumnAccessor["Variants"] = "variants";
    ETableColumnAccessor["GeneName"] = "gene.name";
    ETableColumnAccessor["Ancestry"] = "ancestry";
    ETableColumnAccessor["CollapsingModelName"] = "collapsingModel.name";
    ETableColumnAccessor["PValue"] = "pvalue";
    ETableColumnAccessor["Samples"] = "samples";
    ETableColumnAccessor["QVCases"] = "QVCases";
    ETableColumnAccessor["CaseFrequency"] = "caseFrequency";
    ETableColumnAccessor["QVControls"] = "QVControls";
    ETableColumnAccessor["ControlFrequency"] = "controlFrequency";
    ETableColumnAccessor["OddsRatio"] = "oddsRatio";
    ETableColumnAccessor["OddsRatioLCI"] = "oddsRatioLCI";
    ETableColumnAccessor["OddsRatioUCI"] = "oddsRatioUCI";
})(ETableColumnAccessor || (ETableColumnAccessor = {}));
export const TABLE_COLUMNS_HEADERS = {
    [ETableColumnAccessor.Variants]: 'Link to variants',
    [ETableColumnAccessor.GeneName]: 'Gene',
    [ETableColumnAccessor.Ancestry]: 'Ancestry',
    [ETableColumnAccessor.CollapsingModelName]: 'Collapsing model',
    [ETableColumnAccessor.PValue]: 'P value',
    [ETableColumnAccessor.Samples]: 'No. participants',
    [ETableColumnAccessor.QVCases]: 'No. cases with QV',
    [ETableColumnAccessor.CaseFrequency]: '% cases with QV',
    [ETableColumnAccessor.QVControls]: 'No. controls with QV',
    [ETableColumnAccessor.ControlFrequency]: '% controls with QV',
    [ETableColumnAccessor.OddsRatio]: 'Odds ratio',
    [ETableColumnAccessor.OddsRatioLCI]: 'Odds ratio LCI',
    [ETableColumnAccessor.OddsRatioUCI]: 'Odds ratio UCI',
};
/**
 * Get table columns
 *
 * @param descriptions - Text descriptions for the columns
 * @param referrerData - Referrer data for the variants link
 * @param columnsData - Associations data for the variants link
 * @param hasAncestryData - Boolean value which determines if ancestry data exists
 * @returns formatted table columns
 */
export const getTableColumns = (descriptions, referrerData, columnsData, hasAncestryData) => [
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.Variants],
        accessor: ETableColumnAccessor.Variants,
        className: styles.alignLeft,
        Cell: TableCellLink({
            additionalData: columnsData,
            traitsType: ETraitsTypes.Binary,
            referrerData,
        }, createTableQVLink),
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.VARIANTS_LINK,
        exported: false,
        sortType: (rowA, rowB) => viewVariantsSort(rowA, rowB, Boolean(FEATURE_FLAGS.PUBLIC_VERSION)),
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.GeneName],
        accessor: ETableColumnAccessor.GeneName,
        className: styles.alignLeft,
        Cell: TableCellLink({
            levelResult: ELevelResult.Gene,
            traitsType: ETraitsTypes.Binary,
            referrerData,
        }, createTableGeneLink),
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.GENE,
        sortType: geneLinkSort,
    },
    ...(hasAncestryData ? [
        {
            Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.Ancestry],
            accessor: ETableColumnAccessor.Ancestry,
            description: descriptions[ETableColumnAccessor.Ancestry],
            fixed: true,
            width: COMMON_TABLE_COLUMN_WIDTHS.ANCESTRY,
            sortType: basicSort,
        },
    ] : []),
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.CollapsingModelName],
        accessor: ETableColumnAccessor.CollapsingModelName,
        description: descriptions[ETableColumnAccessor.CollapsingModelName],
        className: styles.alignLeft,
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.MODEL,
        sortType: basicSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.PValue],
        accessor: ETableColumnAccessor.PValue,
        description: descriptions[ETableColumnAccessor.PValue],
        Cell: TableCellPValue,
        sortType: numericSort,
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.P_VALUE,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.Samples],
        accessor: ETableColumnAccessor.Samples,
        description: descriptions[ETableColumnAccessor.Samples],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: TableWidths.WIDTH_160,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.QVCases],
        accessor: ETableColumnAccessor.QVCases,
        description: descriptions[ETableColumnAccessor.QVCases],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_CASES_WITH_QV,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.CaseFrequency],
        accessor: ETableColumnAccessor.CaseFrequency,
        description: descriptions[ETableColumnAccessor.CaseFrequency],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        exported: {
            Header: 'Proportion cases with QV',
            accessor: ETableColumnAccessor.CaseFrequency,
        },
        width: COMMON_TABLE_COLUMN_WIDTHS.PERCENT_CASES_WITH_QV,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.QVControls],
        accessor: ETableColumnAccessor.QVControls,
        description: descriptions[ETableColumnAccessor.QVControls],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_CONTROLS_WITH_QV,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.ControlFrequency],
        accessor: ETableColumnAccessor.ControlFrequency,
        description: descriptions[ETableColumnAccessor.ControlFrequency],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        exported: {
            Header: 'Proportion controls with QV',
            accessor: ETableColumnAccessor.ControlFrequency,
        },
        width: COMMON_TABLE_COLUMN_WIDTHS.PERCENT_CONTROLS_OF_QV,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.OddsRatio],
        accessor: ETableColumnAccessor.OddsRatio,
        description: descriptions[ETableColumnAccessor.OddsRatio],
        Cell: TableCellScientificNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.ODDS_RATIO,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.OddsRatioLCI],
        accessor: ETableColumnAccessor.OddsRatioLCI,
        description: descriptions[ETableColumnAccessor.OddsRatioLCI],
        Cell: TableCellScientificNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.ODDS_RATIO_LCI,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.OddsRatioUCI],
        accessor: ETableColumnAccessor.OddsRatioUCI,
        description: descriptions[ETableColumnAccessor.OddsRatioUCI],
        Cell: TableCellScientificNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.ODDS_RATIO_UCI,
    },
];
export const getWesWgsTableColumns = (descriptions, referrerData) => [
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.GeneName],
        accessor: ETableColumnAccessor.GeneName,
        className: styles.alignLeft,
        Cell: TableCellLink({
            levelResult: ELevelResult.Gene,
            traitsType: ETraitsTypes.Binary,
            referrerData,
        }, createTableGeneLink),
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.GENE,
        sortType: geneLinkSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.CollapsingModelName],
        accessor: ETableColumnAccessor.CollapsingModelName,
        description: descriptions[ETableColumnAccessor.CollapsingModelName],
        className: styles.alignLeft,
        fixed: true,
        width: COMMON_TABLE_COLUMN_WIDTHS.MODEL,
        sortType: basicSort,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.PValue],
        accessor: ETableColumnAccessor.PValue,
        description: descriptions[ETableColumnAccessor.PValue],
        Cell: TableCellPValue,
        sortType: numericSort,
        className: styles.alignLeft,
        width: COMMON_TABLE_COLUMN_WIDTHS.P_VALUE,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.Samples],
        accessor: ETableColumnAccessor.Samples,
        description: descriptions[ETableColumnAccessor.Samples],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: TableWidths.WIDTH_160,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.QVCases],
        accessor: ETableColumnAccessor.QVCases,
        description: descriptions[ETableColumnAccessor.QVCases],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_CASES_WITH_QV,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.CaseFrequency],
        accessor: ETableColumnAccessor.CaseFrequency,
        description: descriptions[ETableColumnAccessor.CaseFrequency],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        exported: {
            Header: 'Proportion cases with QV',
            accessor: ETableColumnAccessor.CaseFrequency,
        },
        width: COMMON_TABLE_COLUMN_WIDTHS.PERCENT_CASES_WITH_QV,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.QVControls],
        accessor: ETableColumnAccessor.QVControls,
        description: descriptions[ETableColumnAccessor.QVControls],
        Cell: TableCellInteger,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.NUMBER_OF_CONTROLS_WITH_QV,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.ControlFrequency],
        accessor: ETableColumnAccessor.ControlFrequency,
        description: descriptions[ETableColumnAccessor.ControlFrequency],
        Cell: TableCellPercent,
        sortType: numericSort,
        className: styles.alignRight,
        exported: {
            Header: 'Proportion controls with QV',
            accessor: ETableColumnAccessor.ControlFrequency,
        },
        width: COMMON_TABLE_COLUMN_WIDTHS.PERCENT_CONTROLS_OF_QV,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.OddsRatio],
        accessor: ETableColumnAccessor.OddsRatio,
        description: descriptions[ETableColumnAccessor.OddsRatio],
        Cell: TableCellScientificNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.ODDS_RATIO,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.OddsRatioLCI],
        accessor: ETableColumnAccessor.OddsRatioLCI,
        description: descriptions[ETableColumnAccessor.OddsRatioLCI],
        Cell: TableCellScientificNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.ODDS_RATIO_LCI,
    },
    {
        Header: TABLE_COLUMNS_HEADERS[ETableColumnAccessor.OddsRatioUCI],
        accessor: ETableColumnAccessor.OddsRatioUCI,
        description: descriptions[ETableColumnAccessor.OddsRatioUCI],
        Cell: TableCellScientificNumeric,
        sortType: numericSort,
        className: styles.alignRight,
        width: COMMON_TABLE_COLUMN_WIDTHS.ODDS_RATIO_UCI,
    },
];
